import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Form,
  LoadingSpinner,
  MultiselectFormField,
  PhoneNumberFormField,
  TextFormField,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveIncidentTypeCategoriesQueryOptions } from "@/lib/data/react-query/matters/matter-type-category";
import {
  referralProfileQueryOptions,
  useUpdateReferralProfile,
} from "@/lib/data/react-query/firms/referral-profile";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { US_STATES } from "@/lib/utils/states";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  email: z.union([
    z.string().trim().email("Enter a valid email address"),
    z.literal(""),
  ]),
  phoneNumber: z.string(),
  practiceAreaIds: z.array(z.string()),
  serviceAreas: z.array(z.string()),
  dateModified: z.string(),
});

export function ReferralProfileSettings() {
  const { hasActionPermission } = useUser();
  const canEdit = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmReferralProfile,
    ACTION_PERMISSION_VALUES.update,
  );
  const profileQuery = useQuery(referralProfileQueryOptions());
  const categoriesQuery = useQuery(
    retrieveIncidentTypeCategoriesQueryOptions(),
  );
  const updateProfile = useUpdateReferralProfile();
  const [saved, setSaved] = useState(false);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      phoneNumber: "",
      practiceAreaIds: [],
      serviceAreas: [],
      dateModified: "",
    },
  });
  const profile = profileQuery.data?.data;
  const isDirty = form.formState.isDirty;

  useEffect(() => {
    if (profile && !isDirty) {
      form.reset({
        email: profile.email ?? "",
        phoneNumber: profile.phoneNumber ?? "",
        practiceAreaIds: profile.practiceAreas.map((area) => area.id),
        serviceAreas: profile.serviceAreas,
        dateModified: profile.dateModified,
      });
    }
  }, [profile, isDirty, form]);

  const onSubmit = async (values: z.infer<typeof schema>) => {
    if (!canEdit) return;
    setSaved(false);
    try {
      const result = await updateProfile.mutateAsync({
        dateModified: values.dateModified,
        email: values.email.trim() || null,
        phoneNumber: values.phoneNumber || null,
        practiceAreaIds: values.practiceAreaIds,
        serviceAreas: values.serviceAreas,
      });
      form.reset({
        email: result.data.email ?? "",
        phoneNumber: result.data.phoneNumber ?? "",
        practiceAreaIds: result.data.practiceAreas.map((area) => area.id),
        serviceAreas: result.data.serviceAreas,
        dateModified: result.data.dateModified,
      });
      setSaved(true);
    } catch {
      // Keep the user's edits available to retry after a failed request.
    }
  };

  const practiceAreas = new Map(
    (profile?.practiceAreas ?? []).map((area) => [area.id, area.label]),
  );
  for (const area of categoriesQuery.data?.data ?? []) {
    practiceAreas.set(area.id, area.label);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Directory profile</CardTitle>
      </CardHeader>
      <CardContent>
        {profileQuery.isPending ? (
          <div className="flex items-center gap-2 py-8" role="status">
            <LoadingSpinner /> Loading referral profile…
          </div>
        ) : !profile ? (
          <div role="alert" className="space-y-3">
            <p>Could not load your referral profile.</p>
            <Button
              variant="outline"
              onClick={() => {
                void profileQuery.refetch();
              }}
            >
              Retry
            </Button>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <fieldset
                disabled={!canEdit || updateProfile.isPending}
                className="space-y-5"
              >
                <div className="grid gap-5 md:grid-cols-2">
                  <TextFormField
                    label="Referral email"
                    type="email"
                    register={form.register("email")}
                  />
                  <PhoneNumberFormField
                    label="Referral phone"
                    register={form.register("phoneNumber")}
                  />
                  <MultiselectFormField
                    label="Practice areas"
                    register={form.register("practiceAreaIds")}
                    options={Array.from(practiceAreas, ([value, label]) => ({
                      value,
                      label,
                    }))}
                    placeholder="Select practice areas"
                    disabled={!canEdit || updateProfile.isPending}
                  />
                  <MultiselectFormField
                    label="Service states"
                    register={form.register("serviceAreas")}
                    options={Array.from(
                      new Set([...US_STATES, ...profile.serviceAreas]),
                    ).map((value) => ({ value, label: value }))}
                    placeholder="Select service states"
                    disabled={!canEdit || updateProfile.isPending}
                  />
                </div>
              </fieldset>
              {categoriesQuery.isError && (
                <p role="alert" className="text-sm">
                  Could not load practice areas.{" "}
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => {
                      void categoriesQuery.refetch();
                    }}
                  >
                    Retry
                  </Button>
                </p>
              )}
              {!canEdit && (
                <p className="text-muted-foreground text-sm">
                  You do not have permission to edit the referral profile.
                </p>
              )}
              {updateProfile.isError && (
                <p role="alert" className="text-destructive text-sm">
                  Could not save your referral profile. Your changes have been
                  kept. Please try again.
                </p>
              )}
              {saved && !isDirty && (
                <p role="status" className="text-muted-foreground text-sm">
                  Referral profile saved.
                </p>
              )}
              {canEdit && (
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={
                      !isDirty ||
                      updateProfile.isPending ||
                      profileQuery.isFetching
                    }
                    onClick={async () => {
                      const result = await profileQuery.refetch();
                      if (!result.isSuccess) return;
                      const latest = result.data.data;
                      form.reset({
                        email: latest.email ?? "",
                        phoneNumber: latest.phoneNumber ?? "",
                        practiceAreaIds: latest.practiceAreas.map(
                          (area) => area.id,
                        ),
                        serviceAreas: latest.serviceAreas,
                        dateModified: latest.dateModified,
                      });
                      updateProfile.reset();
                      setSaved(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!isDirty || updateProfile.isPending}
                  >
                    {updateProfile.isPending ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              )}
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
}
